import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../components/ReactTable/Table";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  FilledInput,
  FormControl,
  TextField
} from "@mui/material";
import { useTableData } from "../ReactTable/useTableData";
import { useContentRepositoryItems } from "../../hooks/data/contentRepository";
import { camelCase } from "lodash";
import { contentOwners, enterKeyCode } from "../../constants/index";
import { Search } from "@mui/icons-material";
import { PropTypes } from "prop-types";
import ExpandableFilters from "../Filters/ExpandableFilters";

const getColumns = (t, visibleColumns) => {
  const columns = {
    name: {
      Header: t("table.columnHeader.name"),
      accessor: "name",
      id: "name"
    },
    repository: {
      Header: t("table.columnHeader.repository"),
      Cell: ({
        row: {
          original: { repository }
        }
      }) => {
        let repositoriesList = [];
        if (repository) {
          if (Array.isArray(repository)) {
            repository.forEach(rep =>
              repositoriesList.push(<div key={camelCase(rep)}>{rep}</div>)
            );
          } else {
            repositoriesList.push(repository);
          }
        }
        return repositoriesList;
      },
      id: "libraryName",
      accessor: "libraryName"
    },
    course: {
      Header: t("table.columnHeader.course"),
      Cell: ({
        row: {
          original: { isGraceHill, trainingObjects }
        }
      }) => {
        let courseList = [];
        trainingObjects.forEach(trainingObject => {
          if (
            (isGraceHill &&
              trainingObject.contentOwner === contentOwners.master) ||
            !isGraceHill // If it's a GH item, we only add the course name if it's a master course
          ) {
            courseList.push(
              <div key={trainingObject.id}>{trainingObject.name}</div>
            );
          }
        });
        return courseList;
      },
      id: "trainingObjects.name",
      accessor: "trainingObjects.name"
    },
    contentableType: {
      Header: t("table.columnHeader.type"),
      accessor: "contentableType",
      id: "contentableType"
    }
  };
  return visibleColumns.map(columnName => columns[columnName]);
};

const initialFiltersState = {
  searchInput: "",
  repository: "",
  type: ""
};

const RepositoryItemDropDownFilter = ({
  labelId,
  label,
  value,
  onChange,
  options
}) => {
  return (
    <FormControl variant="filled" fullWidth>
      <InputLabel htmlFor={labelId}>{label}</InputLabel>
      <Select
        input={<FilledInput id={labelId} value={value} />}
        onChange={onChange}
        disableUnderline
      >
        <MenuItem value="" />
        {options &&
          options.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

const RepositoryItemFilters = ({
  itemTypeOptions,
  repositoryOptions,
  onFilter,
  modalOpen,
  expandable
}) => {
  const { t } = useTranslation();

  const [filtersValues, setFiltersValues] = useState(initialFiltersState);

  useEffect(() => {
    setFiltersValues(initialFiltersState);
    onFilter(initialFiltersState);
  }, [modalOpen]);

  const handleFilterChange = field => event => {
    setFiltersValues({
      ...filtersValues,
      [field]: event && event.target ? event.target.value : event
    });
  };

  const handleClearFilters = () => {
    setFiltersValues(initialFiltersState);
    onFilter(initialFiltersState);
  };

  const expandableFilters = (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={5} lg={5}>
        <RepositoryItemDropDownFilter
          labelId="add-from-repository-repo-filter"
          label={t("courseBuilder.addFromRepositoryRepoFilterLabel")}
          value={filtersValues.repository}
          onChange={handleFilterChange("repository")}
          options={repositoryOptions}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={5}>
        <RepositoryItemDropDownFilter
          labelId="add-from-repository-type-filter"
          label={t("courseBuilder.addFromRepositoryTypeFilterLabel")}
          value={filtersValues.type}
          onChange={handleFilterChange("type")}
          options={itemTypeOptions}
        />
      </Grid>
      <Grid item xs={12} md={3} lg={2} sx={{ alignSelf: "center" }}>
        <ButtonGroup align="right">
          <Button color="primary" onClick={() => handleClearFilters()}>
            {t("courseBuilder.addFromRepositoryClearBtn")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => onFilter(filtersValues)}
          >
            {t("courseBuilder.addFromRepositoryFilterBtn")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );

  const searchField = (
    <TextField
      fullWidth
      onChange={handleFilterChange("searchInput")}
      variant="filled"
      InputProps={{
        disableUnderline: true,
        endAdornment: <Search color="action" />,
        onKeyDown: ({ keyCode }) =>
          keyCode === enterKeyCode && onFilter(filtersValues)
      }}
      placeholder={t("courseBuilder.addFromRepositorySearchLabel")}
      value={filtersValues.searchInput}
      hiddenLabel
    />
  );

  return (
    <Box display="flex" flexDirection="column" mb={3}>
      {expandable ? (
        <ExpandableFilters
          expandableContent={expandableFilters}
          staticContent={searchField}
        />
      ) : (
        <>
          <Grid container sx={{ mb: 2 }}>
            <Grid item xs={12} md={9} lg={10}>
              {searchField}
            </Grid>
            <Grid
              item
              md={3}
              lg={2}
              sx={{ display: { xs: "none", md: "block" } }}
            />
          </Grid>
          {expandableFilters}
        </>
      )}
    </Box>
  );
};

const AddFromRepositoryTable = ({
  onSelectRows,
  excludedCourseSectionId,
  itemTypes,
  itemCategories,
  visibleColumns,
  enableExpandableFilters,
  courseOrPolicy
}) => {
  const { fetchContentRepositoryItems } = useContentRepositoryItems({
    courseOrPolicy,
    excludedCourseSectionId
  });
  const {
    data,
    paginationState,
    onStateChange,
    isFetching,
    onApplyExternalFilters
  } = useTableData(fetchContentRepositoryItems);
  const { t } = useTranslation();
  const memoColumns = useMemo(() => getColumns(t, visibleColumns), []);

  return (
    <>
      <RepositoryItemFilters
        itemTypeOptions={itemTypes}
        repositoryOptions={itemCategories}
        onFilter={onApplyExternalFilters}
        modalOpen={open}
        expandable={enableExpandableFilters}
      />

      <Table
        data={data}
        columns={memoColumns}
        onStateChange={onStateChange}
        paginationState={paginationState}
        paginated={true}
        loading={isFetching}
        updateSelection={onSelectRows}
        showSelectAllCheckbox={true}
        rowSelectionDisabled={({ original: { disabled } }) => disabled}
        cellSize="medium"
        maxHeight="unset"
      />
    </>
  );
};

export default AddFromRepositoryTable;

AddFromRepositoryTable.propTypes = {
  visibleColumns: PropTypes.arrayOf(
    PropTypes.oneOf(["name", "repository", "course", "contentableType"])
  ),
  onSelectRows: PropTypes.func,
  /**
   * If true, filters for table will be expandable
   */
  enableExpandableFilters: PropTypes.bool,
  excludedCourseSectionId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])
  /**
   * Contentable types that will be excluded from fetched repository items
   */
};

AddFromRepositoryTable.defaultProps = {
  visibleColumns: ["name", "repository", "course", "contentableType"]
};
